import type { Recommendation } from "@bindery/shared/reader/daily-reading";
import type { ReadingRecommendationsRequest } from "@/contracts/reading-recommendations";
import { readerRange, type ReaderDb } from "@/storage/db";
import {
  isPoolStale,
  poolGeneration,
  readCachedPool,
  unreadCandidates,
  writeCachedPool,
} from "./recommendation-pool";
import { recommendationsRequest } from "./recommendation-request";
import type { RecommendationsAnswer } from "./recommendations-api";

export interface DailyPoolDeps {
  db: ReaderDb;
  request: (
    body: ReadingRecommendationsRequest,
    signal: AbortSignal,
  ) => Promise<RecommendationsAnswer>;
  now: () => number;
}

export interface DailyPoolLoad {
  readerId: string;
  /** The session answered; while it is unavailable only the cache is read. */
  canFetch: boolean;
  /** Fetch even when the cached pool is fresh. */
  force: boolean;
}

export interface DailyPool {
  pool: Recommendation[];
  /** The server could not be reached; `pool` is the last one this device held, or empty. */
  offline: boolean;
}

/**
 * The reader's unread pool: the cached one while it is fresh, else a new one
 * from the route, else the cached one marked offline.
 */
export async function loadDailyPool(
  { readerId, canFetch, force }: DailyPoolLoad,
  { db, request, now }: DailyPoolDeps,
  signal: AbortSignal,
): Promise<DailyPool> {
  const generation = poolGeneration(readerId);
  const [lower, upper] = readerRange(readerId);
  const [fluency, bookmarks, cached] = await Promise.all([
    db.fluency.where("[readerId+word]").between(lower, upper).toArray(),
    db.bookmarks.where("[readerId+readingId]").between(lower, upper).toArray(),
    readCachedPool(db, readerId),
  ]);
  const completed = new Set(
    bookmarks
      .filter((bookmark) => bookmark.completed === true)
      .map((bookmark) => bookmark.readingId),
  );
  const fromCache = (offline: boolean): DailyPool => ({
    pool: cached ? unreadCandidates(cached.candidates, completed) : [],
    offline,
  });

  const at = now();
  if (!force && cached && !isPoolStale(cached, completed, at)) {
    return fromCache(false);
  }
  if (!canFetch) return fromCache(true);

  const answer = await request(
    recommendationsRequest(fluency, bookmarks, at),
    signal,
  );
  switch (answer.kind) {
    case "pool":
      if (!signal.aborted && poolGeneration(readerId) === generation) {
        await writeCachedPool(db, readerId, {
          fetchedAt: at,
          candidates: answer.candidates,
        });
      }
      return {
        pool: unreadCandidates(answer.candidates, completed),
        offline: false,
      };
    case "no_reader":
      return { pool: [], offline: false };
    case "unavailable":
      return fromCache(true);
  }
}
